import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  buttonVariants,
  Input,
  Label,
} from "~/common/redesign-v1";
import { cn } from "~/common/redesign-v1/cn";
import { addInstructions, selectInstructions } from "~/slices/instructions";
import { useAppDispatch, useAppSelector } from "~/store";

/**
 * Redesign leave-guard modal (replaces legacy `patient/modal.tsx` under `data-redesign-v1`).
 * Same flow: ask to save → optionally collect first/last name for a draft → Yes saves / No leaves.
 */

export type LeavePatientModalProps = {
  visible: boolean;
  saveAsADraft: boolean;
  onCancel(): void;
  onConfirm(): void;
  saveChanges(): void;
};

export function RedesignLeavePatientModal({ visible, saveAsADraft, onCancel, onConfirm, saveChanges }: LeavePatientModalProps) {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const instructions = useAppSelector(selectInstructions);
  const [needNames, setNeedNames] = useState(false);

  useEffect(() => {
    if (!visible) setNeedNames(false);
  }, [visible]);

  const firstName = instructions.first_name ?? "";
  const lastName = instructions.last_name ?? "";
  const namesReady = Boolean(firstName.trim() && lastName.trim());

  function handleSave() {
    if (saveAsADraft) {
      if (namesReady) {
        saveChanges();
      } else {
        setNeedNames(true);
      }
      return;
    }
    onCancel();
    saveChanges();
  }

  function handleClose() {
    setNeedNames(false);
    onCancel();
  }

  // Use the shared confirmation title and put the question in the dialog description.
  const descriptionId = saveAsADraft ? "BTN_SAVE_DRAFT_CONTINUE_LATER" : "BTN_SAVE_CONTINUE_LATER";

  return (
    <AlertDialog open={visible} onOpenChange={(open) => !open && handleClose()}>
      <AlertDialogContent
        data-testid="rd-leave-patient-modal"
        className={cn(needNames && "tw-max-w-md")}
        // The name-entry branch has no description. Explicit `undefined` suppresses Radix's accessibility
        // warning without overriding its generated `aria-describedby` in the confirmation branch.
        {...(needNames ? { "aria-describedby": undefined } : {})}
        // Allow Escape / outside to mean "stay" (legacy ×).
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          handleClose();
        }}
        onPointerDownOutside={(event) => {
          event.preventDefault();
          handleClose();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
          handleClose();
        }}
      >
        {needNames ? (
          <>
            <AlertDialogHeader className="tw-text-left">
              <AlertDialogTitle className="tw-text-destructive">
                <FormattedMessage id="REQUIRED_FIELDS_FOR_A_DRAFT" />
              </AlertDialogTitle>
            </AlertDialogHeader>
            <div className="tw-grid tw-gap-3 desktop:tw-grid-cols-2">
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <Label htmlFor="rd-leave-last-name">
                  <FormattedMessage id="PAT_INFO_LAST_NAME" />
                  <span className="tw-text-destructive"> *</span>
                </Label>
                <Input
                  data-matomo-mask
                  id="rd-leave-last-name"
                  name="last_name"
                  error={!lastName.trim()}
                  defaultValue={lastName}
                  placeholder={intl.formatMessage({ id: "PAT_INFO_LAST_NAME_PLACEHOLDER" })}
                  onChange={(e) => dispatch(addInstructions({ last_name: e.target.value }))}
                />
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <Label htmlFor="rd-leave-first-name">
                  <FormattedMessage id="PAT_INFO_FIRST_NAME" />
                  <span className="tw-text-destructive"> *</span>
                </Label>
                <Input
                  data-matomo-mask
                  id="rd-leave-first-name"
                  name="first_name"
                  error={!firstName.trim()}
                  defaultValue={firstName}
                  placeholder={intl.formatMessage({ id: "PAT_INFO_FIRST_NAME_PLACEHOLDER" })}
                  onChange={(e) => dispatch(addInstructions({ first_name: e.target.value }))}
                />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogAction disabled={!namesReady} onClick={handleSave}>
                <FormattedMessage id="BTN_SAVE_DRAFT" />
              </AlertDialogAction>
              <Button type="button" variant="outline" className="tw-w-full" onClick={onConfirm}>
                <FormattedMessage id="bool.false" />
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              {/* Use the existing generic confirmation title shared by other alerts. */}
              <AlertDialogTitle>
                <FormattedMessage id="pat.status.button.confirm" />
              </AlertDialogTitle>
              <AlertDialogDescription>
                <FormattedMessage id={descriptionId} />
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={handleSave}>
                <FormattedMessage id="BTN_SAVE" />
              </AlertDialogAction>
              <button type="button" className={cn(buttonVariants({ variant: "outline" }), "tw-w-full")} onClick={onConfirm}>
                <FormattedMessage id="events.watch_modal.cancel" />
              </button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
